import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import { TrendingUp, BarChart3 } from "lucide-react";

const fmt = (v) => typeof v === "number" ? `$${(v / 1000).toFixed(0)}k` : v;

// Static chart configuration objects (avoid re-creation on render)
const TICK_STYLE = { fontSize: 11 };
const GRID_STYLE = { strokeDasharray: "3 3", stroke: "#e2e8f0" };
const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8 };
const LEGEND_STYLE = { fontSize: 11 };
const DOT_STYLE = { r: 4 };
const ACTIVE_DOT_STYLE = { r: 6 };
const FINANCIAL_FORMATTER = (v) => `$${(v / 1000).toFixed(0)}k`;
const IDENTITY_FORMATTER = (v) => v;

const METRIC_CONFIG = {
  net_profit: { color: "#10b981", label: "hospital.netProfit", format: fmt },
  cumulative_profit: { color: "#059669", label: "hospital.cumProfit", format: fmt },
  valuation: { color: "#8b5cf6", label: "hospital.valuation", format: (v) => `$${(v / 1_000_000).toFixed(1)}M` },
  bed_occupancy: { color: "#0ea5e9", label: "hospital.bedOccupancy", format: (v) => `${v}%` },
  patient_satisfaction: { color: "#ec4899", label: "hospital.patientSat", format: (v) => v?.toFixed(1) },
  staff_satisfaction: { color: "#f59e0b", label: "hospital.staffSat", format: (v) => v?.toFixed(1) },
  reputation: { color: "#f97316", label: "hospital.reputation", format: (v) => v?.toFixed(1) },
  safety_rating: { color: "#22c55e", label: "hospital.safety", format: (v) => v?.toFixed(1) },
};

const CHART_PRESETS = {
  financial: ["net_profit", "cumulative_profit", "valuation"],
  quality: ["patient_satisfaction", "staff_satisfaction", "reputation", "safety_rating"],
  operational: ["bed_occupancy"],
};

export default function PerformanceHistory({ hospitalId, history, hospital }) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState("financial");

  if (!history || history.length === 0) {
    return null;
  }

  const activeMetrics = CHART_PRESETS[preset] || CHART_PRESETS.financial;

  // Prepare data for line chart - add staff_satisfaction and safety_rating from hospital history
  // The history endpoint returns: round_number, valuation, net_profit, cumulative_profit, bed_occupancy, patient_satisfaction, total_beds
  // We need to supplement with additional data if available
  const chartData = history.map((h) => ({
    year: `${t("common.round")} ${h.round_number}`,
    ...h,
  }));

  // Radar chart data for latest round
  const latest = history[history.length - 1];
  const radarData = [
    { metric: t("hospital.patientSat"), value: latest?.patient_satisfaction || 0, fullMark: 100 },
    { metric: t("hospital.reputation"), value: hospital?.reputation || 0, fullMark: 100 },
    { metric: t("hospital.safety"), value: hospital?.safety_rating || 0, fullMark: 100 },
    { metric: t("hospital.staffSat"), value: hospital?.staff_satisfaction || 0, fullMark: 100 },
    { metric: t("hospital.bedOccupancy"), value: latest?.bed_occupancy || 0, fullMark: 100 },
    { metric: t("hospital.marketShare"), value: hospital?.market_share || 0, fullMark: 100 },
  ];

  return (
    <div className="space-y-4" data-testid="performance-history">
      {/* Metric preset selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">{t("perfHistory.title")}</h3>
        </div>
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="perf-preset-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="financial">{t("perfHistory.financial")}</SelectItem>
            <SelectItem value="quality">{t("perfHistory.quality")}</SelectItem>
            <SelectItem value="operational">{t("perfHistory.operational")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Line Chart */}
      <Card>
        <CardContent className="pt-4">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData}>
              <CartesianGrid {...GRID_STYLE} />
              <XAxis dataKey="year" tick={TICK_STYLE} />
              <YAxis
                tickFormatter={preset === "financial" ? FINANCIAL_FORMATTER : IDENTITY_FORMATTER}
                tick={TICK_STYLE}
              />
              <Tooltip
                formatter={(value, name) => {
                  const cfg = METRIC_CONFIG[name];
                  return [cfg ? cfg.format(value) : value, cfg ? t(cfg.label) : name];
                }}
                contentStyle={TOOLTIP_STYLE}
              />
              <Legend
                formatter={(value) => {
                  const cfg = METRIC_CONFIG[value];
                  return cfg ? t(cfg.label) : value;
                }}
                wrapperStyle={LEGEND_STYLE}
              />
              {activeMetrics.map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={METRIC_CONFIG[key]?.color || "#94a3b8"}
                  strokeWidth={2}
                  dot={DOT_STYLE}
                  activeDot={ACTIVE_DOT_STYLE}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Radar Chart - Hospital Profile */}
      {history.length >= 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-violet-500" />
              {t("perfHistory.hospitalProfile")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e2e8f0" />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Radar
                  dataKey="value"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.2}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
